import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  LayersControl,
  useMap,
  Circle,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { getMovingTrafficData } from "../api/positionApi";
import { getAgencies } from "../api/agencyApi";

import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet.heat";

import { Typography, Layout } from "antd";
import logoAyasa from "../assets/ayasa.png";
import logoRenault from "../assets/0emwie0m.png";
// Importar íconos de marcas
import renaultIcon from "../assets/renault.png";
import nissanIcon from "../assets/nissan.svg";
import fotonIcon from "../assets/foton.svg";
import baicIcon from "../assets/baic.svg";
import RoadsTrafficPanel from "../components/RoadsTrafficPanel";

const { Title } = Typography;
const { Content } = Layout;
const { BaseLayer } = LayersControl;

/** -- ESTILOS inline SENCILLOS PARA HEADER -- */
const logoContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "15px",
};
const logoStyle = {
  height: "32px",
  objectFit: "contain",
};
const renaultLogoStyle = {
  height: "40px",
  objectFit: "contain",
};
const titleContainerStyle = {
  display: "flex",
  alignItems: "center",
  flex: 1,
  justifyContent: "center",
};
const colors = {
  primary: "#4096ff",
  text: "#2e3a59",
};

// -----------------------------------------------------------------------------
// 1) Controladores de Mapa
// -----------------------------------------------------------------------------

/** Componente para inicializar el mapa y escuchar zoomend */
const MapController = ({ setMapRef, onZoomEnd }) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      setMapRef(map);
      map.on("zoomend", onZoomEnd);

      return () => {
        map.off("zoomend", onZoomEnd);
      };
    }
  }, [map, setMapRef, onZoomEnd]);

  return null;
};

/** Botón de pantalla completa */
const FullscreenControl = ({ mapContainerId }) => {
  const map = useMap();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    const mapContainer = document.getElementById(mapContainerId);

    if (
      !document.fullscreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (mapContainer.requestFullscreen) {
        mapContainer.requestFullscreen();
      } else if (mapContainer.webkitRequestFullscreen) {
        mapContainer.webkitRequestFullscreen();
      } else if (mapContainer.msRequestFullscreen) {
        mapContainer.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, [mapContainerId]);

  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(
        document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
      );
      setTimeout(() => {
        if (map) {
          map.invalidateSize();
        }
      }, 200);
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, [map]);

  useEffect(() => {
    const FullscreenButtonControl = L.Control.extend({
      options: {
        position: "topleft",
      },
      onAdd: () => {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control fullscreen-control"
        );
        const button = L.DomUtil.create("a", "fullscreen-button", container);

        const enterFullscreenIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>`;
        const exitFullscreenIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>`;

        button.innerHTML = isFullscreen
          ? exitFullscreenIcon
          : enterFullscreenIcon;

        button.href = "#";
        button.title = isFullscreen
          ? "Salir de pantalla completa"
          : "Pantalla completa";
        button.setAttribute("role", "button");
        button.setAttribute(
          "aria-label",
          isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"
        );

        L.DomEvent.on(button, "click", function (e) {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          toggleFullscreen();
        });

        return container;
      },
    });

    const control = new FullscreenButtonControl();
    control.addTo(map);
    return () => {
      map.removeControl(control);
    };
  }, [map, isFullscreen, toggleFullscreen, mapContainerId]);

  return null;
};

/** Capa de calor usando leaflet.heat */
const HeatmapLayer = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !points || points.length === 0) return;

    // Formatear puntos para leaflet.heat
    const heatPoints = points.map((point) => [
      point.lat,
      point.lng,
      point.intensity * 2.5, // Amplificar la intensidad
    ]);

    // Crear la capa de heat
    const heatLayer = L.heatLayer(heatPoints, {
      radius: 30,
      blur: 20,
      maxZoom: 17,
      minOpacity: 0.4,
      max: 1.0,
      gradient: {
        0.1: "#0000ff",
        0.3: "#00ffff",
        0.5: "#00ff00",
        0.7: "#ffff00",
        0.9: "#ff0000",
      },
    });

    heatLayer.addTo(map);

    return () => {
      map.removeLayer(heatLayer);
    };
  }, [map, points]);

  return null;
};

// -----------------------------------------------------------------------------
// 2) Componente para Agencias
// -----------------------------------------------------------------------------

function getBrandName(brandId) {
  switch (parseInt(brandId)) {
    case 1:
      return "NISSAN";
    case 2:
      return "FOTON";
    case 3:
      return "RENAULT";
    case 4:
      return "BAIC";
    default:
      return "Desconocido";
  }
}

function createBrandIcon(brandId) {
  const size = 48;
  let iconUrl;
  switch (parseInt(brandId)) {
    case 1: // NISSAN
      iconUrl = nissanIcon;
      break;
    case 2: // FOTON
      iconUrl = fotonIcon;
      break;
    case 3: // RENAULT
      iconUrl = renaultIcon;
      break;
    case 4: // BAIC
      iconUrl = baicIcon;
      break;
    default:
      iconUrl = renaultIcon;
  }

  return new L.Icon({
    iconUrl,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
    className: `brand-icon brand-${brandId} traffic-map-icon`,
    zIndexOffset: 1000,
  });
}

/** Renderiza las agencias sin clúster */
const AgencyLayer = ({
  agencies = [],
  showRenaultRadius,
  showNissanRadius,
}) => {
  // Filtrar agencias según los checkboxes activos
  const filteredAgencies = agencies.filter((agency) => {
    if (!agency.latitude || !agency.longitude) return false;

    const brandId =
      agency.brandId ||
      (agency.brandDealer ? agency.brandDealer.id : null) ||
      0;
    const brandName = getBrandName(brandId);

    // Si no hay ningún radio activo, mostrar todas las agencias
    if (!showRenaultRadius && !showNissanRadius) return true;

    // Si hay algún radio activo, solo mostrar las agencias correspondientes
    if (showRenaultRadius && brandName === "RENAULT") return true;
    if (showNissanRadius && brandName === "NISSAN") return true;

    return false;
  });

  return (
    <>
      {filteredAgencies.map((agency) => {
        const brandId =
          agency.brandId ||
          (agency.brandDealer ? agency.brandDealer.id : null) ||
          0;
        const brandName = getBrandName(brandId);
        const showRadius =
          (brandName === "RENAULT" && showRenaultRadius) ||
          (brandName === "NISSAN" && showNissanRadius);

        return (
          <React.Fragment key={`agency-${agency.id || Math.random()}`}>
            <Marker
              position={[
                parseFloat(agency.latitude),
                parseFloat(agency.longitude),
              ]}
              icon={createBrandIcon(brandId)}
            >
              <Popup>
                <div style={{ padding: "12px", textAlign: "center" }}>
                  <h3 style={{ margin: "0 0 8px" }}>{agency.name}</h3>
                  <p style={{ margin: 0 }}>
                    <strong>Marca:</strong> {brandName}
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>Dirección:</strong> {agency.address || ""}
                  </p>
                </div>
              </Popup>
            </Marker>
            {showRadius && (
              <Circle
                center={[
                  parseFloat(agency.latitude),
                  parseFloat(agency.longitude),
                ]}
                radius={5000} // 5km en metros
                pathOptions={{
                  color: brandName === "RENAULT" ? "#000000" : "#FF0000",
                  fillColor: brandName === "RENAULT" ? "#000000" : "#FF0000",
                  fillOpacity: 0.1,
                  weight: 2,
                  opacity: 0.5,
                }}
              >
                <Popup>
                  <div style={{ padding: "12px", textAlign: "center" }}>
                    <h3 style={{ margin: "0 0 8px" }}>{agency.name}</h3>
                    <p style={{ margin: 0 }}>
                      <strong>Radio de 5km</strong>
                    </p>
                    <p style={{ margin: 0 }}>
                      <strong>Marca:</strong> {brandName}
                    </p>
                  </div>
                </Popup>
              </Circle>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

// -----------------------------------------------------------------------------
// 3) Controles tipo panel: Visualización y Agencias
// -----------------------------------------------------------------------------

const VisualizationControl = ({ visualizationType, onChange }) => {
  const map = useMap();

  useEffect(() => {
    const visualizationControl = L.control({ position: "topright" });

    visualizationControl.onAdd = function () {
      const container = L.DomUtil.create(
        "div",
        "leaflet-control leaflet-bar visualization-control"
      );

      const iconButton = L.DomUtil.create("a", "visualization-icon", container);
      iconButton.href = "#";
      iconButton.title = "Tipo de Visualización";

      // Cargar FontAwesome si no está
      if (!document.getElementById("font-awesome")) {
        const faLink = document.createElement("link");
        faLink.id = "font-awesome";
        faLink.rel = "stylesheet";
        faLink.href =
          "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css";
        document.head.appendChild(faLink);

        setTimeout(() => {
          iconButton.innerHTML = '<i class="fa fa-layer-group"></i>';
        }, 100);
      } else {
        iconButton.innerHTML = '<i class="fa fa-layer-group"></i>';
      }

      const panel = L.DomUtil.create("div", "visualization-panel", container);
      panel.style.display = "none";

      const header = L.DomUtil.create("div", "panel-header", panel);
      header.innerHTML = "<strong>Tipo de Visualización</strong>";

      const optionsContainer = L.DomUtil.create(
        "div",
        "visualization-options",
        panel
      );

      // Opción cluster
      const clusterLabel = L.DomUtil.create("label", "", optionsContainer);
      const clusterInput = L.DomUtil.create("input", "", clusterLabel);
      clusterInput.type = "radio";
      clusterInput.name = "visualization";
      clusterInput.value = "cluster";
      clusterInput.checked = visualizationType === "cluster";
      L.DomUtil.create("span", "", clusterLabel).innerHTML =
        "Concentración de vehículos";

      // Opción heatmap
      const heatmapLabel = L.DomUtil.create("label", "", optionsContainer);
      const heatmapInput = L.DomUtil.create("input", "", heatmapLabel);
      heatmapInput.type = "radio";
      heatmapInput.name = "visualization";
      heatmapInput.value = "heatmap";
      heatmapInput.checked = visualizationType === "heatmap";
      L.DomUtil.create("span", "", heatmapLabel).innerHTML = "Mapa de calor";

      // Toggle panel al hacer clic en el icono
      L.DomEvent.on(iconButton, "click", function (e) {
        L.DomEvent.preventDefault(e);
        L.DomEvent.stopPropagation(e);

        if (panel.style.display === "none") {
          panel.style.display = "block";
          // Cerrar el otro panel si está abierto
          const agencyPanel = document.querySelector(".agency-panel");
          if (agencyPanel && agencyPanel.style.display !== "none") {
            agencyPanel.style.display = "none";
          }
        } else {
          panel.style.display = "none";
        }
      });

      // Manejo de cambio de radio
      L.DomEvent.on(clusterInput, "change", function (e) {
        if (e.target.checked) {
          onChange("cluster");
          panel.style.display = "none";
        }
      });
      L.DomEvent.on(heatmapInput, "change", function (e) {
        if (e.target.checked) {
          onChange("heatmap");
          panel.style.display = "none";
        }
      });

      // Cerrar panel al hacer clic fuera
      L.DomEvent.on(document, "click", function (e) {
        if (!container.contains(e.target)) {
          panel.style.display = "none";
        }
      });

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      return container;
    };

    visualizationControl.addTo(map);

    return () => {
      map.removeControl(visualizationControl);
      L.DomEvent.off(document, "click");
    };
  }, [map, visualizationType, onChange]);

  return null;
};

const AgencyToggleControl = ({
  showAgencies,
  onChange,
  showRenaultRadius,
  setShowRenaultRadius,
  showNissanRadius,
  setShowNissanRadius,
}) => {
  const map = useMap();

  useEffect(() => {
    const agencyControl = L.control({ position: "topright" });

    agencyControl.onAdd = function () {
      const container = L.DomUtil.create(
        "div",
        "leaflet-control leaflet-bar agency-control"
      );

      const iconButton = L.DomUtil.create("a", "agency-icon", container);
      iconButton.href = "#";
      iconButton.title = "Agencias";

      if (document.getElementById("font-awesome")) {
        iconButton.innerHTML = '<i class="fa fa-store"></i>';
      } else {
        iconButton.innerHTML = "🏢";
      }

      const panel = L.DomUtil.create("div", "agency-panel", container);
      panel.style.display = "none";

      const header = L.DomUtil.create("div", "panel-header", panel);
      header.innerHTML = "<strong>Agencias</strong>";

      const optionsContainer = L.DomUtil.create("div", "agency-options", panel);

      // Checkbox para mostrar/ocultar agencias
      const agencyLabel = L.DomUtil.create("label", "", optionsContainer);
      const agencyInput = L.DomUtil.create("input", "", agencyLabel);
      agencyInput.type = "checkbox";
      agencyInput.checked = showAgencies;
      L.DomUtil.create("span", "", agencyLabel).innerHTML = "Mostrar Agencias";

      // Checkbox para radio Renault
      const renaultLabel = L.DomUtil.create("label", "", optionsContainer);
      const renaultInput = L.DomUtil.create("input", "", renaultLabel);
      renaultInput.type = "checkbox";
      renaultInput.checked = showRenaultRadius;
      L.DomUtil.create("span", "", renaultLabel).innerHTML =
        "Radio 5km Renault";

      // Checkbox para radio Nissan
      const nissanLabel = L.DomUtil.create("label", "", optionsContainer);
      const nissanInput = L.DomUtil.create("input", "", nissanLabel);
      nissanInput.type = "checkbox";
      nissanInput.checked = showNissanRadius;
      L.DomUtil.create("span", "", nissanLabel).innerHTML = "Radio 5km Nissan";

      L.DomEvent.on(iconButton, "click", function (e) {
        L.DomEvent.preventDefault(e);
        L.DomEvent.stopPropagation(e);

        if (panel.style.display === "none") {
          panel.style.display = "block";
          // Cerrar panel de visualización si está abierto
          const visualizationPanel = document.querySelector(
            ".visualization-panel"
          );
          if (
            visualizationPanel &&
            visualizationPanel.style.display !== "none"
          ) {
            visualizationPanel.style.display = "none";
          }
        } else {
          panel.style.display = "none";
        }
      });

      L.DomEvent.on(agencyInput, "change", function (e) {
        onChange(e.target.checked);
      });

      L.DomEvent.on(renaultInput, "change", function (e) {
        setShowRenaultRadius(e.target.checked);
      });

      L.DomEvent.on(nissanInput, "change", function (e) {
        setShowNissanRadius(e.target.checked);
      });

      L.DomEvent.on(document, "click", function (e) {
        if (!container.contains(e.target)) {
          panel.style.display = "none";
        }
      });

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      return container;
    };

    agencyControl.addTo(map);

    return () => {
      map.removeControl(agencyControl);
      L.DomEvent.off(document, "click");
    };
  }, [
    map,
    showAgencies,
    onChange,
    showRenaultRadius,
    setShowRenaultRadius,
    showNissanRadius,
    setShowNissanRadius,
  ]);

  return null;
};

// -----------------------------------------------------------------------------
// 4) Componente principal: MovingTrafficDashboard
// -----------------------------------------------------------------------------

const MovingTrafficDashboard = () => {
  // Insertar CSS para el spinner
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Estados
  const [positions, setPositions] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [showAgencies, setShowAgencies] = useState(true);
  const [showRenaultRadius, setShowRenaultRadius] = useState(false);
  const [showNissanRadius, setShowNissanRadius] = useState(false);
  const [visualizationType, setVisualizationType] = useState("cluster");

  const mapRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(7);
  const [isMapReady, setIsMapReady] = useState(false);

  const [isZoomChanging, setIsZoomChanging] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // Para centrar en ubicación externa (ej: RoadTrafficPanel)
  const [temporaryMarker, setTemporaryMarker] = useState(null);

  // Generar ID único para el contenedor del mapa
  const mapId = useRef(
    `traffic-map-${Math.random().toString(36).substr(2, 9)}`
  );

  // Cargar agencias al montar
  useEffect(() => {
    const loadAgencies = async () => {
      try {
        const response = await getAgencies();
        if (response && response.data) {
          setAgencies(response.data);
        }
      } catch (error) {
        console.error("Error al cargar las agencias:", error);
      }
    };
    loadAgencies();
  }, []);

  // Centro en Ecuador
  const mapCenter = [-1.1807, -78.7055];

  // Diferentes niveles de zoom
  const zoomLevels = useMemo(
    () => ({
      lejano: 8, // Vista general
      medio: 13, // Vista ciudad
      cercano: 16, // Vista detallada
    }),
    []
  );

  // Ajustar la ref del mapa
  const setMapRef = useCallback((map) => {
    if (!map) return;
    mapRef.current = map;
    setCurrentZoom(map.getZoom());
    setIsMapReady(true);

    // Activar animaciones
    map.options.fadeAnimation = true;
    map.options.zoomAnimation = true;
    map.options.markerZoomAnimation = true;

    // Activar scroll y doble clic
    map.scrollWheelZoom.enable();
    map.doubleClickZoom.enable();

    // Forzar un refresh en la animación
    map.on("zoomanim", () => {
      setTimeout(() => {
        if (map) {
          map.invalidateSize();
        }
      }, 500);
    });

    // Asegurar que el mapa se "acomode" bien al crearse
    setTimeout(() => {
      if (map) {
        map.invalidateSize();
      }
    }, 1000);
  }, []);

  // Calcular gridSize en la API según zoom
  const getGridSize = useCallback(
    (zoom) => {
      if (zoom >= 17) return 0.001; // Extremadamente detallado
      if (zoom >= 16) return 0.002; // Muy detallado
      if (zoom >= 15) return 0.003; // Detallado
      if (zoom >= zoomLevels.cercano) return 0.004; // Nivel cercano
      if (zoom >= 14) return 0.007; // Más detalle en zoom 14
      if (zoom <= zoomLevels.lejano) return 0.035; // Vista general, mantener igual
      if (zoom <= zoomLevels.medio) return 0.015; // Vista ciudad, mantener igual
      return 0.01; // Nivel intermedio
    },
    [zoomLevels]
  );

  // Función para "limpiar" datos
  const sanitizeData = useCallback((data) => {
    if (!Array.isArray(data)) return [];

    // Procesamiento inicial de los datos
    const processedData = data.map((item) => ({
      latitude: parseFloat(item.latitude || item.lat || 0),
      longitude: parseFloat(item.longitude || item.lng || 0),
      point_count: parseInt(item.point_count || item.vehicle_count || 0, 10),
      unique_vehicles: parseInt(item.unique_vehicles || 0, 10),
      avg_speed: parseFloat(item.avg_speed || item.speed || 0),
    }));

    // Filtrar puntos que probablemente no estén en carreteras
    return processedData.filter((point) => {
      // Criterio 1: Puntos con velocidades promedio muy bajas (menor a 3 km/h)
      // suelen ser estacionamientos o lugares que no son vías
      if (point.avg_speed < 3) {
        return false;
      }

      // Criterio 2: Si la relación registros/vehículos es extremadamente alta,
      // probablemente son vehículos estacionados y no representan tráfico real
      const recordsPerVehicle = point.unique_vehicles
        ? point.point_count / point.unique_vehicles
        : 0;

      if (recordsPerVehicle > 100) {
        // Si cada vehículo tiene más de 100 registros en el mismo punto
        return false;
      }

      // Criterio 3: Puntos con muy pocos vehículos únicos pero muchos registros
      // probablemente no representan tráfico real
      if (point.unique_vehicles < 5 && point.point_count > 500) {
        return false;
      }

      return true;
    });
  }, []);

  // Definición centralizada de niveles de tráfico y sus colores
  const TRAFFIC_LEVELS = {
    MUY_ALTO: { name: "Muy Alto", color: "#DC143C", minScore: 8 }, // Rojo oscuro
    ALTO: { name: "Alto", color: "#FF4040", minScore: 6 }, // Rojo
    MEDIO: { name: "Medio", color: "#FFA07A", minScore: 4 }, // Naranja
    MEDIO_BAJO: { name: "Medio-Bajo", color: "#F0E68C", minScore: 2 }, // Amarillo
    BAJO: { name: "Bajo", color: "#90EE90", minScore: 0 }, // Verde
  };

  // Función para obtener color de tráfico según el conteo de puntos
  const getTrafficColor = useCallback(
    (count) => {
      if (count < 50) return TRAFFIC_LEVELS.BAJO.color;
      if (count < 100) return TRAFFIC_LEVELS.MEDIO_BAJO.color;
      if (count < 200) return TRAFFIC_LEVELS.MEDIO.color;
      if (count < 500) return TRAFFIC_LEVELS.ALTO.color;
      return TRAFFIC_LEVELS.MUY_ALTO.color;
    },
    [
      TRAFFIC_LEVELS.BAJO.color,
      TRAFFIC_LEVELS.MEDIO_BAJO.color,
      TRAFFIC_LEVELS.MEDIO.color,
      TRAFFIC_LEVELS.ALTO.color,
      TRAFFIC_LEVELS.MUY_ALTO.color,
    ]
  );

  // Función para obtener nivel de tráfico a partir de una puntuación
  // Mantenemos esta función con un comentario eslint-disable ya que puede ser útil en el futuro
  // eslint-disable-next-line no-unused-vars
  const getTrafficLevelFromScore = useCallback(
    (score) => {
      if (score >= TRAFFIC_LEVELS.MUY_ALTO.minScore)
        return TRAFFIC_LEVELS.MUY_ALTO;
      if (score >= TRAFFIC_LEVELS.ALTO.minScore) return TRAFFIC_LEVELS.ALTO;
      if (score >= TRAFFIC_LEVELS.MEDIO.minScore) return TRAFFIC_LEVELS.MEDIO;
      if (score >= TRAFFIC_LEVELS.MEDIO_BAJO.minScore)
        return TRAFFIC_LEVELS.MEDIO_BAJO;
      return TRAFFIC_LEVELS.BAJO;
    },
    [
      TRAFFIC_LEVELS.MUY_ALTO,
      TRAFFIC_LEVELS.ALTO,
      TRAFFIC_LEVELS.MEDIO,
      TRAFFIC_LEVELS.MEDIO_BAJO,
      TRAFFIC_LEVELS.BAJO,
    ]
  );

  // Crear ícono del clúster
  const createClusterIcon = useCallback(
    (cluster) => {
      const totalCount = cluster
        .getAllChildMarkers()
        .reduce((acc, marker) => acc + (marker.options.count || 1), 0);

      // Ajustamos el tamaño según la cantidad de puntos
      const size = totalCount < 100 ? 40 : totalCount < 1000 ? 50 : 60;

      // Para valores grandes, mostrar abreviaciones como K
      const displayText =
        totalCount >= 1000
          ? (totalCount / 1000).toFixed(1) + "K"
          : totalCount.toString();

      return L.divIcon({
        html: `
          <div style="
            background-color: ${getTrafficColor(totalCount)};
            width: ${size}px;
            height: ${size}px;
            line-height: ${size}px;
            text-align: center;
            border-radius: 50%;
            color: white;
            font-size: ${totalCount >= 1000 ? "14px" : "12px"};
            font-weight: bold;
            text-shadow: 1px 1px 1px rgba(0,0,0,0.5);
          ">
            ${displayText}
          </div>
        `,
        className: "custom-cluster-icon",
        iconSize: L.point(size, size),
      });
    },
    [getTrafficColor]
  );

  // Crear íconos para marcadores de tráfico
  const markerIcons = useMemo(() => {
    const icons = {};
    // Crear diferentes tamaños de íconos según el valor de point_count
    // Usamos más niveles para tener mayor granularidad
    for (let i = 1; i <= 20; i++) {
      // Tamaño variable según la importancia y nivel de zoom
      const currentZoom = mapRef.current ? mapRef.current.getZoom() : 7;
      let size;
      if (currentZoom >= 16) {
        // En zoom muy cercano, íconos más pequeños para ver mejor las vías
        size = i <= 5 ? 18 : i <= 10 ? 22 : i <= 15 ? 26 : 30;
      } else if (currentZoom >= 15) {
        // En zoom cercano, íconos de tamaño medio
        size = i <= 5 ? 22 : i <= 10 ? 26 : i <= 15 ? 32 : 38;
      } else {
        // En zoom lejano, íconos de tamaño normal
        size = i <= 5 ? 28 : i <= 10 ? 34 : i <= 15 ? 40 : 46;
      }

      // En lugar de usar una fórmula con factor, usamos valores más realistas
      // basados en rangos típicos de point_count
      let displayValue;
      if (i === 1) displayValue = 50;
      else if (i === 2) displayValue = 100;
      else if (i === 3) displayValue = 200;
      else if (i === 4) displayValue = 300;
      else if (i === 5) displayValue = 400;
      else if (i === 6) displayValue = 500;
      else if (i === 7) displayValue = 700;
      else if (i === 8) displayValue = 900;
      else if (i === 9) displayValue = 1200;
      else if (i === 10) displayValue = 1500;
      else if (i === 11) displayValue = 2000;
      else if (i === 12) displayValue = 2500;
      else if (i === 13) displayValue = 3000;
      else if (i === 14) displayValue = 3500;
      else if (i === 15) displayValue = 4000;
      else if (i === 16) displayValue = 5000;
      else if (i === 17) displayValue = 7000;
      else if (i === 18) displayValue = 9000;
      else if (i === 19) displayValue = 12000;
      else displayValue = 15000;

      // Para valores grandes, mostrar abreviaciones como K
      const displayText =
        displayValue >= 1000
          ? (displayValue / 1000).toFixed(1) + "K"
          : displayValue.toString();

      // Usar getTrafficColor para mantener coherencia con los popups
      const backgroundColor = getTrafficColor(displayValue);

      icons[i] = L.divIcon({
        html: `
          <div style="
            background-color: ${backgroundColor}; 
            width: ${size}px; 
            height: ${size}px; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            border-radius: 50%; 
            color: white; 
            font-weight: bold;
            font-size: ${i <= 10 ? "10px" : "12px"};
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
            border: 1px solid rgba(255,255,255,0.5);
          ">
            ${displayText}
          </div>`,
        className: "",
        iconSize: [size, size],
        // Almacenamos el valor real para usar en los clusters
        realValue: displayValue,
      });
    }
    return icons;
  }, [getTrafficColor]);

  const getMarkerIcon = useCallback(
    (count) => {
      // Función optimizada para determinar el nivel del ícono basado en el número real de registros
      let index;

      // Rangos de valores definidos de manera más clara
      const ranges = [
        { max: 50, index: 1 },
        { max: 100, index: 2 },
        { max: 200, index: 3 },
        { max: 300, index: 4 },
        { max: 400, index: 5 },
        { max: 500, index: 6 },
        { max: 700, index: 7 },
        { max: 900, index: 8 },
        { max: 1200, index: 9 },
        { max: 1500, index: 10 },
        { max: 2000, index: 11 },
        { max: 2500, index: 12 },
        { max: 3000, index: 13 },
        { max: 3500, index: 14 },
        { max: 4000, index: 15 },
        { max: 5000, index: 16 },
        { max: 7000, index: 17 },
        { max: 9000, index: 18 },
        { max: 12000, index: 19 },
        { max: Infinity, index: 20 },
      ];

      // Encontrar el primer rango que aplica
      const range = ranges.find((r) => count < r.max);
      index = range ? range.index : 20;

      return markerIcons[index] || markerIcons[1];
    },
    [markerIcons]
  );

  // Crear contenido del popup más atractivo
  const createPopupContent = useCallback(
    (position) => {
      const { latitude, longitude, point_count, unique_vehicles, avg_speed } =
        position;

      // Calcular relación entre registros y vehículos
      const recordsPerVehicle = unique_vehicles
        ? (point_count / unique_vehicles).toFixed(1)
        : 0;

      // Sistema de puntuación multifactorial para determinar la congestión (máx 10 puntos)
      // Factor 1: Velocidad (menor velocidad = mayor congestión) - Max 3 puntos
      const speedFactor =
        avg_speed < 20 ? 3 : avg_speed < 30 ? 2 : avg_speed < 40 ? 1 : 0;

      // Factor 2: Cantidad de registros (más registros = mayor congestión) - Max 3 puntos
      const countFactor =
        point_count > 1000
          ? 3
          : point_count > 500
          ? 2
          : point_count > 200
          ? 1
          : 0;

      // Factor 3: Cantidad de vehículos únicos (más vehículos = mayor congestión) - Max 3 puntos
      const vehicleFactor =
        unique_vehicles > 50
          ? 3
          : unique_vehicles > 30
          ? 2
          : unique_vehicles > 15
          ? 1
          : 0;

      // Factor 4: Permanencia (mayor ratio registros/vehículo = mayor congestión) - Max 2 puntos
      const ratioFactor =
        recordsPerVehicle > 25 ? 2 : recordsPerVehicle > 15 ? 1 : 0;

      // Puntuación total de congestión (máx 11 puntos)
      const congestionScore =
        speedFactor + countFactor + vehicleFactor + ratioFactor;

      // Determinar nivel de tráfico basado en la puntuación multifactorial
      let trafficLevel;
      if (congestionScore >= 8) {
        trafficLevel = {
          name: "Muy Alto",
          color: TRAFFIC_LEVELS.MUY_ALTO.color,
        };
      } else if (congestionScore >= 6) {
        trafficLevel = {
          name: "Alto",
          color: TRAFFIC_LEVELS.ALTO.color,
        };
      } else if (congestionScore >= 4) {
        trafficLevel = {
          name: "Medio",
          color: TRAFFIC_LEVELS.MEDIO.color,
        };
      } else if (congestionScore >= 2) {
        trafficLevel = {
          name: "Medio-Bajo",
          color: TRAFFIC_LEVELS.MEDIO_BAJO.color,
        };
      } else {
        trafficLevel = {
          name: "Bajo",
          color: TRAFFIC_LEVELS.BAJO.color,
        };
      }

      // Estimar la confiabilidad de este punto como ubicación de tráfico real
      // basado en una combinación de factores
      const speedVariation = Math.abs(45 - avg_speed) / 45; // Qué tan lejos está de una velocidad "normal" de 45 km/h
      const hasReasonableVehicles =
        unique_vehicles >= 10 && unique_vehicles <= 1000;
      const hasReasonableRecords = point_count >= 50 && point_count <= 10000;
      const hasReasonableRatio =
        recordsPerVehicle >= 5 && recordsPerVehicle <= 50;

      // Calcular confiabilidad (0-100%)
      const reliabilityScore = Math.min(
        100,
        Math.max(
          0,
          (hasReasonableVehicles ? 30 : 0) +
            (hasReasonableRecords ? 30 : 0) +
            (hasReasonableRatio ? 20 : 0) +
            (20 - speedVariation * 20) // 0-20 puntos basado en qué tan normal es la velocidad
        )
      );

      // Clasificar confiabilidad
      let reliability = { text: "", color: "" };
      if (reliabilityScore >= 90) {
        reliability = { text: "Muy Alta", color: "#00C853" }; // Verde fuerte
      } else if (reliabilityScore >= 75) {
        reliability = { text: "Alta", color: "#64DD17" }; // Verde
      } else if (reliabilityScore >= 60) {
        reliability = { text: "Media", color: "#FFD600" }; // Amarillo
      } else if (reliabilityScore >= 40) {
        reliability = { text: "Baja", color: "#FF9100" }; // Naranja
      } else {
        reliability = { text: "Muy Baja", color: "#FF3D00" }; // Rojo
      }

      return (
        <div
          style={{
            fontFamily: "Arial, sans-serif",
            padding: "10px",
            minWidth: "250px",
          }}
        >
          <h3
            style={{
              margin: "0 0 10px",
              borderBottom: "2px solid #4096ff",
              paddingBottom: "6px",
              color: "#2e3a59",
              textAlign: "center",
            }}
          >
            Punto de Tráfico
          </h3>

          <div
            style={{
              backgroundColor: trafficLevel.color,
              color: "white",
              padding: "5px 8px",
              borderRadius: "4px",
              marginBottom: "12px",
              fontWeight: "bold",
              textAlign: "center",
              fontSize: "15px",
            }}
          >
            Nivel de Tráfico: {trafficLevel.name}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              backgroundColor: "#f8f9fa",
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                Vehículos Únicos:
              </div>
              <div style={{ fontSize: "14px" }}>{unique_vehicles || 0}</div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                Total Registros:
              </div>
              <div style={{ fontSize: "14px" }}>{point_count || 0}</div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                Registros/Vehículo:
              </div>
              <div style={{ fontSize: "14px" }}>{recordsPerVehicle}</div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                Velocidad Media:
              </div>
              <div style={{ fontSize: "14px" }}>
                {typeof avg_speed === "number"
                  ? avg_speed.toFixed(1)
                  : parseFloat(avg_speed || 0).toFixed(1)}{" "}
                km/h
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "5px",
                paddingTop: "5px",
                borderTop: "1px dashed #ddd",
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                Confiabilidad del Punto:
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: reliability.color,
                  fontWeight: "bold",
                }}
              >
                {reliability.text} ({reliabilityScore}%)
              </div>
            </div>
          </div>

          <div
            style={{
              fontSize: "11px",
              color: "#666",
              marginTop: "10px",
              padding: "5px",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
              textAlign: "center",
            }}
          >
            Ubicación: {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </div>

          {reliabilityScore < 60 && (
            <div
              style={{
                fontSize: "11px",
                color: "#d32f2f", // Rojo de alerta
                marginTop: "5px",
                padding: "5px",
                backgroundColor: "#ffebee", // Fondo rojo suave
                borderRadius: "4px",
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              Nota: Este punto podría no estar ubicado en una vía principal
            </div>
          )}
        </div>
      );
    },
    [
      TRAFFIC_LEVELS.MUY_ALTO.color,
      TRAFFIC_LEVELS.ALTO.color,
      TRAFFIC_LEVELS.MEDIO.color,
      TRAFFIC_LEVELS.MEDIO_BAJO.color,
      TRAFFIC_LEVELS.BAJO.color,
    ]
  );

  // FETCH con throttling (máx cada 3s)
  const fetchPositions = useCallback(async () => {
    if (!isMapReady || !mapRef.current) return;

    const now = Date.now();
    // Si hace <3s que se consultó y ya tenemos datos, omitimos
    if (now - lastFetchTime < 3000 && positions.length > 0) {
      return;
    }

    // Solo mostramos spinner si efectivamente vamos a hacer fetch
    setIsZoomChanging(true);
    setLastFetchTime(now);

    try {
      const bounds = mapRef.current.getBounds();
      const zoom = mapRef.current.getZoom();

      const response = await getMovingTrafficData({
        gridSize: getGridSize(zoom),
        minDensity: 3,
        maxResults: 6000,
        includeOutsideZones: zoom <= zoomLevels.lejano ? "false" : "true",
        minSpeed: 5, // Añadimos un filtro de velocidad mínima para excluir vehículos estacionados
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
      });

      let dataToProcess = response?.data?.data; // Ahora los datos están anidados bajo data.data

      // Si hay metadatos, podemos mostrarlos en consola para debugging
      if (response?.data?.meta) {
        console.debug("Metadatos tráfico:", response.data.meta);
      }

      if (!dataToProcess || dataToProcess.length === 0) {
        // Datos dummy de fallback
        dataToProcess = [
          {
            latitude: -2.1894,
            longitude: -79.8845,
            point_count: 75,
            unique_vehicles: 12,
            avg_speed: 45,
          },
          {
            latitude: -2.199,
            longitude: -79.894,
            point_count: 120,
            unique_vehicles: 25,
            avg_speed: 35,
          },
          {
            latitude: -0.2299,
            longitude: -78.5249,
            point_count: 200,
            unique_vehicles: 30,
            avg_speed: 28,
          },
        ];
      }

      // Aumentado el límite de puntos máximos en frontend
      const maxPoints = 8000; // Antes 5000
      if (dataToProcess.length > maxPoints) {
        dataToProcess = dataToProcess
          .sort((a, b) => (b.point_count || 0) - (a.point_count || 0))
          .slice(0, maxPoints);
      }

      const processed = sanitizeData(dataToProcess);

      // Mejorado: Filtrado adicional para eliminar duplicados por cercanía
      // y puntos que están muy próximos entre sí
      const filteredPositions = [];
      // Hacemos minDistanceMeters dinámico según el nivel de zoom
      const minDistanceMeters = mapRef.current
        ? mapRef.current.getZoom() >= 17
          ? 5 // Extremadamente cercano: solo 5m entre puntos
          : mapRef.current.getZoom() >= 16
          ? 10 // Muy cercano: permitir puntos a 10m (antes 15m)
          : mapRef.current.getZoom() >= 15
          ? 15 // Cercano: permitir puntos a 15m (antes 20m)
          : mapRef.current.getZoom() >= 14
          ? 20 // Medio-cercano: 20m (antes 25m)
          : 30 // Valor por defecto: 30m
        : 30;
      const degToMeters = 111000; // Aproximación: 1 grado ≈ 111km

      // Ordenamos por importancia (point_count descendente)
      const sortedPositions = [...processed].sort(
        (a, b) => b.point_count - a.point_count
      );

      // Iteramos desde el más importante al menos importante
      for (const position of sortedPositions) {
        // Verificamos si este punto está demasiado cerca de alguno ya aceptado
        const isTooClose = filteredPositions.some((existing) => {
          const latDiff =
            Math.abs(existing.latitude - position.latitude) * degToMeters;
          const lngDiff =
            Math.abs(existing.longitude - position.longitude) *
            degToMeters *
            Math.cos((existing.latitude * Math.PI) / 180);
          const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
          return distance < minDistanceMeters;
        });

        if (!isTooClose) {
          filteredPositions.push(position);
        }
      }

      // Dividir los marcadores en grupos de importancia
      const importantMarkers = [];
      const regularMarkers = [];

      filteredPositions.forEach((position) => {
        // Considerar marcadores importantes los que tienen
        // una combinación de velocidad baja y muchos vehículos
        const isImportant =
          (position.avg_speed < 40 && position.unique_vehicles > 20) || // Tráfico con varios vehículos (menos restrictivo)
          (position.avg_speed < 30 && position.unique_vehicles > 15) || // Tráfico lento con pocos vehículos (menos restrictivo)
          (mapRef.current &&
            mapRef.current.getZoom() >= 15 &&
            position.point_count > 50) || // En zoom alto, los que tienen al menos cierta relevancia
          position.point_count > 200; // Umbral más bajo para cantidad de registros

        if (isImportant) {
          importantMarkers.push(position);
        } else {
          regularMarkers.push(position);
        }
      });

      // Función para crear un marcador
      const createMarker = (position) => {
        const { latitude, longitude, point_count } = position;
        const id = `pos-${latitude.toFixed(6)}-${longitude.toFixed(6)}`;

        // Aseguramos que se use el valor real como count para los clusters
        return (
          <Marker
            key={id}
            position={[latitude, longitude]}
            icon={getMarkerIcon(point_count)}
            count={point_count} // Este es el valor real que se usa en los clusters
          >
            <Popup>{createPopupContent(position)}</Popup>
          </Marker>
        );
      };

      // Crear todos los marcadores importantes, son prioritarios
      const importantMarkerElements = importantMarkers.map(createMarker);

      // Limitar la cantidad de marcadores regulares si hay demasiados
      // Primero ordenamos los marcadores regulares por importancia
      const sortedRegularMarkers = regularMarkers.sort(
        (a, b) => b.point_count - a.point_count
      );

      // Calculamos cuántos marcadores regulares mostrar
      // Aumentamos la cantidad de marcadores regulares a mostrar
      const maxRegularMarkers = Math.max(8000 - importantMarkers.length, 0); // Actualizado para ser consistente con maxMarkers

      // Si hay muchos puntos, tomamos una muestra representativa
      // Esto evita que la visualización se sobrecargue con demasiados marcadores
      let sampledRegularMarkers = [];

      if (sortedRegularMarkers.length > maxRegularMarkers) {
        // Tomamos un mayor porcentaje de los marcadores importantes
        const topMarkers = sortedRegularMarkers.slice(
          0,
          Math.floor(maxRegularMarkers * 0.8) // Antes: 0.7
        );

        // Y tomamos una muestra más representativa del resto
        const remainingMarkers = sortedRegularMarkers.slice(
          Math.floor(maxRegularMarkers * 0.8) // Antes: 0.7
        );
        const samplingStep = Math.max(
          1,
          Math.floor(remainingMarkers.length / (maxRegularMarkers * 0.2)) // Antes: 0.3
        );

        for (let i = 0; i < remainingMarkers.length; i += samplingStep) {
          if (
            sampledRegularMarkers.length <
            maxRegularMarkers - topMarkers.length
          ) {
            sampledRegularMarkers.push(remainingMarkers[i]);
          }
        }

        // Combinamos ambos conjuntos
        sampledRegularMarkers = [...topMarkers, ...sampledRegularMarkers];
      } else {
        sampledRegularMarkers = sortedRegularMarkers;
      }

      // Crear los marcadores regulares
      const regularMarkerElements = sampledRegularMarkers.map(createMarker);

      // Mostrar cantidad de marcadores en consola para debugging
      console.debug(
        `Marcadores: ${importantMarkerElements.length} importantes + ${
          regularMarkerElements.length
        } regulares = ${
          importantMarkerElements.length + regularMarkerElements.length
        } total (de ${positions.length} posiciones)`
      );

      setPositions(filteredPositions);
    } catch (error) {
      console.error("Error obteniendo datos de tráfico:", error);
      // Si no teníamos nada, usar fallback
      if (positions.length === 0) {
        setPositions([
          {
            latitude: -2.1894,
            longitude: -79.8845,
            point_count: 50,
            unique_vehicles: 10,
            avg_speed: 40,
          },
        ]);
      }
    } finally {
      setIsZoomChanging(false);
    }
  }, [
    isMapReady,
    mapRef,
    lastFetchTime,
    positions,
    getGridSize,
    sanitizeData,
    zoomLevels,
    getMarkerIcon,
    createPopupContent,
  ]);

  // Manejar zoom
  const handleZoomEnd = useCallback(() => {
    if (!mapRef.current) return;
    const newZoom = mapRef.current.getZoom();
    // Si el zoom difiere al menos 1 nivel
    if (Math.abs(newZoom - currentZoom) >= 1) {
      setCurrentZoom(newZoom);
      // Hacemos un fetch con un pequeño retraso
      const timer = setTimeout(() => {
        fetchPositions();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentZoom, fetchPositions]);

  // Máximo para colorear
  const maxCount = useMemo(() => {
    if (positions.length === 0) return 1;

    // Obtenemos el valor máximo real de point_count
    const calculatedMax = Math.max(...positions.map((p) => p.point_count || 1));

    // Usamos un valor mínimo de 5000 para asegurar un buen rango de colores
    // y un máximo realista para evitar que unos pocos outliers afecten la escala
    return Math.min(Math.max(calculatedMax, 5000), 20000);
  }, [positions]);

  // Config de MarkerClusterGroup
  const clusterOptions = useMemo(
    () => ({
      chunkedLoading: true,
      chunkInterval: 300,
      chunkDelay: 150,
      maxClusterRadius: (zoom) => {
        // Radio de agrupamiento muy dinámico basado en el zoom
        // A mayor zoom (más cerca), menor radio de agrupamiento
        if (zoom >= 17) return 1; // Prácticamente sin agrupamiento (extremadamente reducido)
        if (zoom >= 16) return 3; // Casi sin agrupamiento (antes 5)
        if (zoom >= 15) return 5; // Muy reducido (antes 10)
        if (zoom >= zoomLevels.cercano) return 10; // Nivel cercano (antes 15)
        if (zoom >= 14) return 20; // Intermedio alto (antes 30)
        if (zoom >= zoomLevels.medio) return 40; // Nivel de ciudad (antes 50)
        if (zoom >= 12) return 50; // Intermedio bajo (antes 60)
        if (zoom >= 10) return 60; // Semifar (antes 70)
        if (zoom >= zoomLevels.lejano) return 70; // Nivel lejano (antes 80)
        return 90; // Muy lejano (antes 100)
      },
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      removeOutsideVisibleBounds: true,
      disableClusteringAtZoom: 15, // Desactivar agrupamiento a zoom de nivel 15 o mayor (antes 16)
      iconCreateFunction: createClusterIcon,
      animate: false,
      animateAddingMarkers: false,
      singleMarkerMode: false,
      maxZoom: 18,
      spiderfyDistanceMultiplier: 2,
      // Algoritmo personalizado para determinar si un marcador debe unirse a un cluster
      polygonOptions: {
        fillColor: "#bbf",
        color: "#46f",
        weight: 1,
        opacity: 0.5,
        fillOpacity: 0.2,
      },
    }),
    [zoomLevels, createClusterIcon]
  );

  // Renderizar marcadores de tráfico
  const renderPositionMarkers = useMemo(() => {
    if (!positions.length || !clusterOptions) return null;

    // Aumentamos el límite de marcadores a mostrar
    const maxMarkers = 8000; // Antes 6000
    const limitedPositions =
      positions.length > maxMarkers
        ? positions
            .slice()
            .sort((a, b) => (b.point_count || 0) - (a.point_count || 0))
            .slice(0, maxMarkers)
        : positions;

    // Mejorado: Filtrado adicional para eliminar duplicados por cercanía
    // y puntos que están muy próximos entre sí
    const filteredPositions = [];
    // Hacemos minDistanceMeters dinámico según el nivel de zoom
    const minDistanceMeters = mapRef.current
      ? mapRef.current.getZoom() >= 17
        ? 5 // Extremadamente cercano: solo 5m entre puntos
        : mapRef.current.getZoom() >= 16
        ? 10 // Muy cercano: permitir puntos a 10m (antes 15m)
        : mapRef.current.getZoom() >= 15
        ? 15 // Cercano: permitir puntos a 15m (antes 20m)
        : mapRef.current.getZoom() >= 14
        ? 20 // Medio-cercano: 20m (antes 25m)
        : 30 // Valor por defecto: 30m
      : 30;
    const degToMeters = 111000; // Aproximación: 1 grado ≈ 111km

    // Ordenamos por importancia (point_count descendente)
    const sortedPositions = [...limitedPositions].sort(
      (a, b) => b.point_count - a.point_count
    );

    // Iteramos desde el más importante al menos importante
    for (const position of sortedPositions) {
      // Verificamos si este punto está demasiado cerca de alguno ya aceptado
      const isTooClose = filteredPositions.some((existing) => {
        const latDiff =
          Math.abs(existing.latitude - position.latitude) * degToMeters;
        const lngDiff =
          Math.abs(existing.longitude - position.longitude) *
          degToMeters *
          Math.cos((existing.latitude * Math.PI) / 180);
        const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
        return distance < minDistanceMeters;
      });

      if (!isTooClose) {
        filteredPositions.push(position);
      }
    }

    // Dividir los marcadores en grupos de importancia
    const importantMarkers = [];
    const regularMarkers = [];

    filteredPositions.forEach((position) => {
      // Considerar marcadores importantes los que tienen
      // una combinación de velocidad baja y muchos vehículos
      const isImportant =
        (position.avg_speed < 40 && position.unique_vehicles > 20) || // Tráfico con varios vehículos (menos restrictivo)
        (position.avg_speed < 30 && position.unique_vehicles > 15) || // Tráfico lento con pocos vehículos (menos restrictivo)
        (mapRef.current &&
          mapRef.current.getZoom() >= 15 &&
          position.point_count > 50) || // En zoom alto, los que tienen al menos cierta relevancia
        position.point_count > 200; // Umbral más bajo para cantidad de registros

      if (isImportant) {
        importantMarkers.push(position);
      } else {
        regularMarkers.push(position);
      }
    });

    // Función para crear un marcador
    const createMarker = (position) => {
      const { latitude, longitude, point_count } = position;
      const id = `pos-${latitude.toFixed(6)}-${longitude.toFixed(6)}`;

      // Aseguramos que se use el valor real como count para los clusters
      return (
        <Marker
          key={id}
          position={[latitude, longitude]}
          icon={getMarkerIcon(point_count)}
          count={point_count} // Este es el valor real que se usa en los clusters
        >
          <Popup>{createPopupContent(position)}</Popup>
        </Marker>
      );
    };

    // Crear todos los marcadores importantes, son prioritarios
    const importantMarkerElements = importantMarkers.map(createMarker);

    // Limitar la cantidad de marcadores regulares si hay demasiados
    // Primero ordenamos los marcadores regulares por importancia
    const sortedRegularMarkers = regularMarkers.sort(
      (a, b) => b.point_count - a.point_count
    );

    // Calculamos cuántos marcadores regulares mostrar
    // Aumentamos la cantidad de marcadores regulares a mostrar
    const maxRegularMarkers = Math.max(8000 - importantMarkers.length, 0); // Actualizado para ser consistente con maxMarkers

    // Si hay muchos puntos, tomamos una muestra representativa
    // Esto evita que la visualización se sobrecargue con demasiados marcadores
    let sampledRegularMarkers = [];

    if (sortedRegularMarkers.length > maxRegularMarkers) {
      // Tomamos un mayor porcentaje de los marcadores importantes
      const topMarkers = sortedRegularMarkers.slice(
        0,
        Math.floor(maxRegularMarkers * 0.8) // Antes: 0.7
      );

      // Y tomamos una muestra más representativa del resto
      const remainingMarkers = sortedRegularMarkers.slice(
        Math.floor(maxRegularMarkers * 0.8) // Antes: 0.7
      );
      const samplingStep = Math.max(
        1,
        Math.floor(remainingMarkers.length / (maxRegularMarkers * 0.2)) // Antes: 0.3
      );

      for (let i = 0; i < remainingMarkers.length; i += samplingStep) {
        if (
          sampledRegularMarkers.length <
          maxRegularMarkers - topMarkers.length
        ) {
          sampledRegularMarkers.push(remainingMarkers[i]);
        }
      }

      // Combinamos ambos conjuntos
      sampledRegularMarkers = [...topMarkers, ...sampledRegularMarkers];
    } else {
      sampledRegularMarkers = sortedRegularMarkers;
    }

    // Crear los marcadores regulares
    const regularMarkerElements = sampledRegularMarkers.map(createMarker);

    // Mostrar cantidad de marcadores en consola para debugging
    console.debug(
      `Marcadores: ${importantMarkerElements.length} importantes + ${
        regularMarkerElements.length
      } regulares = ${
        importantMarkerElements.length + regularMarkerElements.length
      } total (de ${positions.length} posiciones)`
    );

    return (
      <MarkerClusterGroup {...clusterOptions}>
        {importantMarkerElements}
        {regularMarkerElements}
      </MarkerClusterGroup>
    );
  }, [positions, clusterOptions, getMarkerIcon, createPopupContent]);

  // Renderizar Heatmap
  const renderHeatmap = useMemo(() => {
    if (!positions.length) return null;

    // Aumentamos el límite de puntos en el heatmap
    const maxHeatmapPoints = 7000; // Antes 5000
    const sortedPositions =
      positions.length > maxHeatmapPoints
        ? positions
            .slice()
            .sort((a, b) => b.point_count - a.point_count)
            .slice(0, maxHeatmapPoints)
        : positions;

    // Calcular maxCount para normalización si no está disponible
    const localMaxCount =
      maxCount || Math.max(...positions.map((p) => p.point_count || 1), 100);

    return (
      <HeatmapLayer
        points={sortedPositions.map((pos) => ({
          lat: pos.latitude,
          lng: pos.longitude,
          // Mejorar el cálculo de intensidad para destacar mejor las áreas con más tráfico
          intensity: Math.min(
            1,
            Math.pow(pos.point_count / localMaxCount, 0.6) * 1.5
          ),
        }))}
      />
    );
  }, [positions, maxCount]);

  // Renderizar Spinner "Analizando Tráfico..."
  const renderLoadingOverlay = useMemo(() => {
    // Solo mostrar si isZoomChanging = true
    if (!isZoomChanging) return null;

    return (
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "8px 16px",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            fontWeight: "500",
            fontSize: "14px",
            color: "#1890ff",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            className="loading-spinner"
            style={{
              width: "16px",
              height: "16px",
              border: "2px solid #f3f3f3",
              borderTop: "2px solid #1890ff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          Analizando Tráfico...
        </div>
      </div>
    );
  }, [isZoomChanging]);

  // Cambio de visualización
  const handleVisualizationChange = useCallback((type) => {
    setVisualizationType(type);
  }, []);

  // 1ra Carga: fetch + Interval
  useEffect(() => {
    if (isMapReady && mapRef.current) {
      // Esperar 1s para asegurar que el mapa esté listo
      const timer = setTimeout(() => {
        fetchPositions();

        // Interval de refresco cada 2 minutos
        const intervalId = setInterval(() => {
          // Solo si la página está visible y no estamos en medio de un zoom
          if (
            mapRef.current &&
            !isZoomChanging &&
            document.visibilityState === "visible"
          ) {
            fetchPositions();
          }
        }, 120000);

        // limpiar interval
        return () => clearInterval(intervalId);
      }, 1000);

      // Limpiar timeout
      return () => clearTimeout(timer);
    }
  }, [isMapReady, fetchPositions, isZoomChanging]);

  // Función para centrar el mapa en un punto (usada desde RoadsTrafficPanel)
  const centerMapOnLocation = useCallback(
    (location) => {
      if (!mapRef.current) return;
      if (!location || typeof location !== "object") return;

      const { lat, lng, zoom, zoomLevel, name, province } = location;
      if (typeof lat !== "number" || typeof lng !== "number") return;

      const finalZoom = zoomLevels[zoomLevel] || zoom || zoomLevels.cercano + 1;

      setIsZoomChanging(true);

      const newMarker = {
        position: [lat, lng],
        name: name || "Ubicación",
        province: province || "",
      };
      setTemporaryMarker(newMarker);

      mapRef.current.flyTo([lat, lng], finalZoom, {
        animate: true,
        duration: 1.5,
      });
      setCurrentZoom(finalZoom);

      // Quitar el marcador tras 10s
      setTimeout(() => {
        setTemporaryMarker(null);
      }, 10000);
    },
    [zoomLevels]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Layout style={{ height: "100vh", overflow: "hidden" }}>
      <Content
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* Cabecera con logotipos */}
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1,
          }}
        >
          <div style={logoContainerStyle}>
            <img src={logoAyasa} style={logoStyle} alt="Logo AYASA" />
            <div style={titleContainerStyle}>
              <Title level={4} style={{ margin: 0, color: colors.text }}>
                Vias De Mayor Circulacion por Provincia
              </Title>
            </div>
            <img
              src={logoRenault}
              style={renaultLogoStyle}
              alt="Logo Renault"
            />
          </div>
        </div>

        {/* Contenedor (mapa + panel lateral) */}
        <div style={{ flex: 1, display: "flex", position: "relative" }}>
          {/* Mapa */}
          <div id={mapId.current} style={{ flex: 1, position: "relative" }}>
            {renderLoadingOverlay}

            <MapContainer
              center={mapCenter}
              zoom={zoomLevels.lejano}
              style={{ height: "100%", width: "100%" }}
              zoomControl={false}
              whenCreated={setMapRef}
              minZoom={6}
              maxZoom={18}
              maxBoundsViscosity={0.5}
              scrollWheelZoom
              doubleClickZoom
              dragging
              attributionControl
              fadeAnimation
              zoomAnimation
              markerZoomAnimation
            >
              {/* Control de Zoom y Referencia */}
              <MapController setMapRef={setMapRef} onZoomEnd={handleZoomEnd} />

              {/* Botón de pantalla completa */}
              <FullscreenControl mapContainerId={mapId.current} />

              {/* Capas Base */}
              <LayersControl position="topright">
                <BaseLayer checked name="Vista de Carretera">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={19}
                    maxNativeZoom={19}
                    keepBuffer={8}
                    updateWhenZooming={false}
                    updateWhenIdle
                  />
                </BaseLayer>
                <BaseLayer name="Vista de Satélite">
                  <TileLayer
                    attribution='&copy; <a href="https://www.esri.com">Esri</a>'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                    maxNativeZoom={19}
                    keepBuffer={8}
                    updateWhenZooming={false}
                    updateWhenIdle
                  />
                </BaseLayer>
                <BaseLayer name="Vista Híbrida">
                  <TileLayer
                    attribution='&copy; <a href="https://www.thunderforest.com/">Thunderforest</a>'
                    url="https://{s}.tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey=6170aad10dfd42a38d4d8c709a536f38"
                    maxZoom={22}
                    maxNativeZoom={18}
                    keepBuffer={8}
                    updateWhenZooming={false}
                    updateWhenIdle
                  />
                </BaseLayer>
              </LayersControl>

              {/* Controles tipo Panel */}
              <VisualizationControl
                visualizationType={visualizationType}
                onChange={handleVisualizationChange}
              />
              <AgencyToggleControl
                showAgencies={showAgencies}
                onChange={setShowAgencies}
                showRenaultRadius={showRenaultRadius}
                setShowRenaultRadius={setShowRenaultRadius}
                showNissanRadius={showNissanRadius}
                setShowNissanRadius={setShowNissanRadius}
              />

              {/* Visualización: cluster o heatmap */}
              {/* eslint-disable-next-line no-use-before-define */}
              {visualizationType === "cluster"
                ? renderPositionMarkers
                : renderHeatmap}

              {/* Agencias si están activadas */}
              {showAgencies && (
                <AgencyLayer
                  agencies={agencies}
                  showRenaultRadius={showRenaultRadius}
                  showNissanRadius={showNissanRadius}
                />
              )}

              {/* Marcador/Círculos temporales */}
              {temporaryMarker && (
                <>
                  <Circle
                    center={temporaryMarker.position}
                    radius={2000}
                    pathOptions={{
                      color: "#1890ff",
                      fillColor: "#1890ff",
                      fillOpacity: 0.2,
                      weight: 2,
                      opacity: 0.8,
                    }}
                  >
                    <Popup>
                      <div style={{ padding: "12px", textAlign: "center" }}>
                        <strong
                          style={{
                            fontSize: "16px",
                            display: "block",
                            marginBottom: "8px",
                          }}
                        >
                          {temporaryMarker.name}
                        </strong>
                        {temporaryMarker.province && (
                          <div style={{ color: "#666", fontSize: "14px" }}>
                            {temporaryMarker.province}
                          </div>
                        )}
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#888",
                            fontStyle: "italic",
                          }}
                        >
                          Zona de tráfico y área de influencia
                        </div>
                      </div>
                    </Popup>
                  </Circle>
                  <Circle
                    center={temporaryMarker.position}
                    radius={3000}
                    pathOptions={{
                      color: "#1890ff",
                      fillColor: "#1890ff",
                      fillOpacity: 0.03,
                      weight: 1,
                      opacity: 0.2,
                      dashArray: "15, 20",
                    }}
                  />
                  <Circle
                    center={temporaryMarker.position}
                    radius={1000}
                    pathOptions={{
                      color: "#1890ff",
                      fillColor: "#1890ff",
                      fillOpacity: 0.35,
                      weight: 2,
                      opacity: 0.8,
                    }}
                  />
                </>
              )}
            </MapContainer>
          </div>

          {/* Panel lateral */}
          <div
            style={{
              width: "600px",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              borderLeft: "1px solid #e8e8e8",
              backgroundColor: "#f5f5f5",
              overflow: "hidden",
            }}
          >
            <RoadsTrafficPanel onCenterMap={centerMapOnLocation} />
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default MovingTrafficDashboard;
